// Usage meter (BILLING-09 quotas): a labelled `role="meter"` bar for "used of limit". The state is
// never shown by colour alone: near/at/over the limit also gets a text label. An unlimited or
// not-included quota renders the value text without a bar. Added by P4-B2 (additive).
import { useId, type ReactNode } from "react";
import { t } from "@ady/i18n";
import { cx } from "../lib";
import { Icon } from "./Icon";

export type UsageMeterState = "ok" | "soft" | "at_limit" | "over" | "unlimited";

export interface UsageMeterProps {
  label: ReactNode;
  used: number;
  /** null = unlimited. 0 = not included (no bar). */
  limit: number | null;
  /** Formatted value ("3 of 5", "120 MB of 2 GB"); also the meter's aria-valuetext. */
  valueText: string;
  state?: UsageMeterState;
  /** Extra line under the bar. */
  note?: ReactNode;
  className?: string;
  "data-testid"?: string;
  "data-quota"?: string;
}

const fill: Record<UsageMeterState, string> = {
  ok: "bg-primary",
  unlimited: "bg-primary",
  soft: "bg-warning-fg",
  at_limit: "bg-danger-fg",
  over: "bg-danger-fg",
};

export function UsageMeter({
  label,
  used,
  limit,
  valueText,
  state = "ok",
  note,
  className,
  "data-testid": testId,
  "data-quota": quota,
}: UsageMeterProps) {
  const labelId = useId();
  const bar = limit !== null && limit > 0;
  const pct = bar ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const stateText =
    state === "soft"
      ? t("meter.soft")
      : state === "at_limit"
        ? t("meter.atLimit")
        : state === "over"
          ? t("meter.over")
          : null;
  return (
    <div
      className={cx("flex flex-col gap-1.5", className)}
      data-testid={testId}
      data-quota={quota}
      data-state={state}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span id={labelId} className="text-md font-medium">
          {label}
        </span>
        <span className="ady-ledger text-sm text-fg-secondary">{valueText}</span>
      </div>
      {bar ? (
        <div
          role="meter"
          aria-labelledby={labelId}
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-valuenow={Math.min(used, limit)}
          aria-valuetext={valueText}
          className="h-2 overflow-hidden rounded-xs border border-subtle bg-sunken"
        >
          <div className={cx("h-full", fill[state])} style={{ width: `${pct}%` }} />
        </div>
      ) : null}
      {stateText ? (
        <span
          className={cx(
            "flex items-center gap-1 text-xs font-medium",
            state === "soft" ? "text-warning-fg" : "text-danger-fg",
          )}
        >
          <Icon name={state === "soft" ? "triangle" : "octagon"} size={14} />
          {stateText}
        </span>
      ) : null}
      {note ? <p className="text-xs text-fg-secondary">{note}</p> : null}
    </div>
  );
}
